import { useEffect, useRef, useState, useCallback } from 'react';
import env from 'shared/constants/env';

/**
 * SSE connection states
 */
const SSE_STATE = {
  IDLE: 'IDLE',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  CLOSED: 'CLOSED',
  ERROR: 'ERROR',
};

/**
 * useNafathSSE — Custom hook to listen for real-time Nafath verification
 * status updates via Server-Sent Events (SSE).
 *
 * The backend endpoint is: GET /v1/auth/nafath-stream?transId=<transId>
 *
 * Events from the server:
 *   - "connected"  → { transId, message }    (initial handshake)
 *   - "status"     → { status, transId }     (COMPLETED | EXPIRED | REJECTED)
 *   - "timeout"    → { message }             (server closed after 120s)
 *
 * @param {Object} options
 * @param {string|null} options.transId - The Nafath transaction ID. Pass null to disable.
 * @param {function} options.onStatus  - Called with { status, transId } on status event.
 * @param {function} [options.onTimeout] - Called when the server times out.
 * @param {function} [options.onError]   - Called on connection error.
 * @param {function} [options.onConnected] - Called when SSE connection is established.
 * @returns {{ sseState: string, close: function }}
 */
export default function useNafathSSE({ transId, onStatus, onTimeout, onError, onConnected }) {
  const [sseState, setSseState] = useState(SSE_STATE.IDLE);
  const eventSourceRef = useRef(null);

  const close = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setSseState(SSE_STATE.CLOSED);
  }, []);

  useEffect(() => {
    // Don't connect if no transId
    if (!transId) {
      return;
    }

    // Build the SSE URL from the environment's v1 API endpoint
    const sseUrl = `${env.API_ENDPOINT}auth/nafath-stream?transId=${encodeURIComponent(transId)}`;

    console.log('[useNafathSSE] Connecting to:', sseUrl);
    setSseState(SSE_STATE.CONNECTING);

    const es = new EventSource(sseUrl);
    eventSourceRef.current = es;

    // Server sends "connected" event when the SSE stream is ready
    es.addEventListener('connected', (event) => {
      console.log('[useNafathSSE] Connected:', event.data);
      setSseState(SSE_STATE.CONNECTED);
      if (onConnected) {
        try {
          onConnected(JSON.parse(event.data));
        } catch (e) {
          onConnected({ message: event.data });
        }
      }
    });

    // Server sends "status" event with Nafath verification result
    es.addEventListener('status', (event) => {
      console.log('[useNafathSSE] Status received:', event.data);
      try {
        const data = JSON.parse(event.data);
        if (onStatus) {
          onStatus(data);
        }
      } catch (e) {
        console.error('[useNafathSSE] Failed to parse status event:', e);
      }
      // Terminal event — server will close the stream, clean up our side too
      close();
    });

    // Server sends "timeout" event when SSE max duration (120s) is reached
    es.addEventListener('timeout', (event) => {
      console.log('[useNafathSSE] Server timeout:', event.data);
      if (onTimeout) {
        try {
          onTimeout(JSON.parse(event.data));
        } catch (e) {
          onTimeout({ message: event.data });
        }
      }
      close();
    });

    // Connection error (network failure, server down, etc.)
    es.onerror = (err) => {
      console.error('[useNafathSSE] EventSource error:', err);
      // EventSource auto-reconnects on transient errors.
      // readyState === 2 (CLOSED) means it gave up.
      if (es.readyState === EventSource.CLOSED) {
        setSseState(SSE_STATE.ERROR);
        if (onError) {
          onError(err);
        }
        close();
      }
    };

    // Cleanup on unmount or when transId changes
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [transId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { sseState, close };
}

export { SSE_STATE };
